import { useCallback, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import LineCheckCircleIcon from 'LuxKit/Icons/line/LineCheckCircleIcon'
import type { EnrichmentStatus, ItineraryUpdate, EnrichmentCompleteEvent, TripItineraryData } from 'tripPlanner/api/types'
import { applyItineraryUpdates, applyEnrichmentUpdate } from 'tripPlanner/utils/itineraryUpdates'
import { useGetItinerary } from 'tripPlanner/hooks/api/chat'
import * as ChatKeys from 'tripPlanner/hooks/reactQueryKeys/chat'
import { computeTripDatesFromStops, formatStopDateRange } from 'tripPlanner/utils/chatDates'

interface UseItineraryUpdatesArgs {
  chatId: number | undefined
  onExpandStops?: (stopIds: Array<number>) => void
  // Ref to track streaming state - prevents refetchOnWindowFocus from overwriting optimistic updates mid-stream
  isStreamingRef: React.RefObject<boolean>
}

export interface ItineraryScrollTarget {
  stopId: number
  dayId?: number
  itemId?: number
}

interface UseItineraryUpdatesReturn {
  itineraryData: TripItineraryData | undefined
  enrichingStopIds: Set<number>
  enrichingItemIds: Set<number>
  scrollTarget: ItineraryScrollTarget | null
  clearScrollTarget: () => void
  // Streaming callbacks receive chatId as first argument to avoid stale closure issues
  handleItineraryUpdates: (chatIdArg: number, updates: Array<ItineraryUpdate>) => void
  handleEnrichmentComplete: (chatIdArg: number, event: EnrichmentCompleteEvent) => void
  handleRefetchItinerary: (chatIdArg: number) => void
  // Non-streaming callbacks use closure chatId (safe for user-initiated actions on current chat)
  refetchCurrentChatItinerary: () => void
}

// ── Enrichment derivation helpers ─────────────────────────────────────

const ENRICHING_STATUSES: ReadonlySet<EnrichmentStatus> = new Set(['pending', 'enriching'])

function isEnriching(entity: { enrichmentStatus?: EnrichmentStatus }): boolean {
  return entity.enrichmentStatus !== undefined && ENRICHING_STATUSES.has(entity.enrichmentStatus)
}

function collectEnrichingIds(
  entities: ReadonlyArray<{ id: number; enrichmentStatus?: EnrichmentStatus }>,
  ids: Set<number>,
): void {
  for (const entity of entities) {
    if (isEnriching(entity)) ids.add(entity.id)
  }
}

/**
 * Returns a referentially-stable Set that only changes identity when its contents change.
 * Prevents unnecessary re-renders of ItineraryContext consumers and refetchInterval churn
 * when itineraryData reference changes but the derived enriching IDs remain the same.
 */
function useStableSet(rawSet: Set<number>): Set<number> {
  const ref = useRef(rawSet)

  if (!setsAreEqual(rawSet, ref.current)) {
    ref.current = rawSet
  }

  return ref.current
}

function setsAreEqual(a: Set<number>, b: Set<number>): boolean {
  if (a.size !== b.size) return false
  for (const id of a) {
    if (!b.has(id)) return false
  }
  return true
}

// ── Hook ──────────────────────────────────────────────────────────────

/**
 * Manages itinerary state for the chat trip planner.
 *
 * Data Flow:
 * 1. React Query (`useGetItinerary`) is the single source of truth for itinerary data
 * 2. During streaming, optimistic updates are applied via `queryClient.setQueryData`
 * 3. After stream completes, `handleRefetchItinerary` invalidates the cache to sync with server
 * 4. `refetchOnWindowFocus` is disabled during streaming to prevent overwriting optimistic updates
 *    - When not streaming, window focus triggers refetch to ensure fresh data
 *
 * Enrichment State (derived, not managed):
 * - `enrichingStopIds` / `enrichingItemIds` are derived from `enrichmentStatus` on itinerary entities
 * - Self-terminating: enrichment finishes → status becomes terminal → Sets empty → refetchInterval stops
 * - Self-resetting: chat switch → itinerary data changes → Sets reflect new chat automatically
 * - `refetchInterval` (3s) acts as a fallback when the stream closes before all enrichment completes
 *   (e.g., 15s BE timeout exceeded). Stream events handle the happy path via optimistic cache patches.
 */
export function useItineraryUpdates({
  chatId,
  onExpandStops,
  isStreamingRef,
}: UseItineraryUpdatesArgs): UseItineraryUpdatesReturn {
  const intl = useIntl()
  const queryClient = useQueryClient()

  // refetchInterval is driven by derived enrichment state. useState + sync-during-render
  // ensures the interval is current on the same render cycle that detects enriching entities,
  // avoiding the one-render lag that a ref-based approach would have.
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)

  const { data: itineraryData } = useGetItinerary(chatId, {
    refetchOnWindowFocus: () => !isStreamingRef.current,
    refetchInterval,
    refetchIntervalInBackground: true,
  })

  // ── Derive enriching entity Sets from itinerary data ──
  // Single pass: collects both stop and item IDs in one iteration.
  // Only 'pending' and 'enriching' are active statuses. undefined = doesn't need enrichment.

  const { stopIds: enrichingStopIdsRaw, itemIds: enrichingItemIdsRaw } = useMemo(() => {
    const stopIds = new Set<number>()
    const itemIds = new Set<number>()
    for (const stop of itineraryData?.stops ?? []) {
      if (isEnriching(stop)) stopIds.add(stop.id)
      for (const day of stop.days) {
        collectEnrichingIds(day.items, itemIds)
      }
      collectEnrichingIds(stop.itemsWithoutDay ?? [], itemIds)
    }
    return { stopIds, itemIds }
  }, [itineraryData])

  const enrichingStopIds = useStableSet(enrichingStopIdsRaw)
  const enrichingItemIds = useStableSet(enrichingItemIdsRaw)

  // Sync refetchInterval with derived enrichment state (setState-during-render pattern).
  // React immediately re-renders before commit, so useGetItinerary gets the correct interval.
  const nextInterval: number | false = (enrichingStopIds.size > 0 || enrichingItemIds.size > 0) ? 3000 : false
  if (nextInterval !== refetchInterval) {
    setRefetchInterval(nextInterval)
  }

  const [scrollTarget, setScrollTarget] = useState<ItineraryScrollTarget | null>(null)

  const clearScrollTarget = useCallback(() => {
    setScrollTarget(null)
  }, [])

  // chatIdArg is passed from streaming code to avoid stale closure (streaming has authoritative chatId)
  const handleItineraryUpdates = useCallback((chatIdArg: number, updates: Array<ItineraryUpdate>) => {
    if (updates.length === 0) return

    if (onExpandStops) {
      const stopIdsToExpand = [...new Set(updates.flatMap(u => {
        if (u.kind === 'day_added') return [u.stopId]
        if (u.kind === 'item_added' || u.kind === 'item_updated') {
          return u.stopVisits?.map(sv => sv.tripStopId) ?? []
        }
        return []
      }))]

      if (stopIdsToExpand.length > 0) {
        onExpandStops(stopIdsToExpand)
      }
    }

    const prevData = queryClient.getQueryData<TripItineraryData>(
      ChatKeys.itineraryDetail(chatIdArg),
    )
    const prevDates = computeTripDatesFromStops(prevData?.stops)

    try {
      queryClient.setQueryData<TripItineraryData | undefined>(
        ChatKeys.itineraryDetail(chatIdArg),
        (prev) => applyItineraryUpdates(prev, updates),
      )
    } catch {
      // Optimistic update failed, will be corrected by server response
    }

    const newData = queryClient.getQueryData<TripItineraryData>(
      ChatKeys.itineraryDetail(chatIdArg),
    )
    const newDates = computeTripDatesFromStops(newData?.stops)

    const tripDatesChanged = prevDates?.startDate !== newDates?.startDate || prevDates?.endDate !== newDates?.endDate

    if (tripDatesChanged) {
      const dateMessage = newDates
        ? intl.formatMessage(
            {
              id: 'OOhdWO0_xvfM9OLPUY-K',
              defaultMessage: 'Trip dates updated to {dateRange}',
              description: 'Snackbar message when trip dates are set or changed',
            },
            { dateRange: formatStopDateRange(newDates.startDate, newDates.endDate) },
          )
        : intl.formatMessage({
            id: 'forhGndOznvrIFs49brO',
            defaultMessage: 'Trip dates removed',
            description: 'Snackbar message when trip dates are cleared in the trip planner',
          })
      showSnackbar(dateMessage, 'success', { icon: <LineCheckCircleIcon /> })
    } else {
      const addedStops = updates.filter((u): u is Extract<ItineraryUpdate, { kind: 'stop_added' }> => u.kind === 'stop_added')
      const updatedStops = updates.filter((u): u is Extract<ItineraryUpdate, { kind: 'stop_updated' }> => u.kind === 'stop_updated')
      const addedItems = updates.filter((u): u is Extract<ItineraryUpdate, { kind: 'item_added' }> => u.kind === 'item_added')
      const updatedItems = updates.filter((u): u is Extract<ItineraryUpdate, { kind: 'item_updated' }> => u.kind === 'item_updated')
      const messages: Array<string> = []
      if (addedStops.length === 1) {
        messages.push(intl.formatMessage(
          {
            id: '1L5oieOS5nXMzEecmbmc',
            defaultMessage: 'Added {placeName}',
            description: 'Snackbar message when a single stop is added to the itinerary',
          },
          {
            placeName: addedStops[0].stop?.placeName ?? intl.formatMessage({
              id: 'yRxcIING_tY4NfEYDlnm',
              defaultMessage: 'stop',
              description: 'Fallback noun for an unnamed itinerary stop in the itinerary',
            }),
          },
        ))
      } else if (addedStops.length > 1) {
        messages.push(intl.formatMessage(
          {
            id: 'gbT2QXUA1pHsHaGJKBv5',
            defaultMessage: 'Added {count, plural, one {# stop} other {# stops}}',
            description: 'Snackbar message when multiple stops are added to the itinerary',
          },
          { count: addedStops.length },
        ))
      }
      if (updatedStops.length === 1) {
        messages.push(intl.formatMessage(
          {
            id: 'o0r6AGrK7nx0i237bl-T',
            defaultMessage: 'Updated {placeName}',
            description: 'Snackbar message when a single stop is updated in the itinerary',
          },
          {
            placeName: updatedStops[0].stop?.placeName ?? intl.formatMessage({
              id: 'yRxcIING_tY4NfEYDlnm',
              defaultMessage: 'stop',
              description: 'Fallback noun for an unnamed itinerary stop in the itinerary',
            }),
          },
        ))
      } else if (updatedStops.length > 1) {
        messages.push(intl.formatMessage(
          {
            id: 'J5eoKi5xlhBeMup0zBFO',
            defaultMessage: 'Updated {count, plural, one {# stop} other {# stops}}',
            description: 'Snackbar message when multiple stops are updated in the itinerary',
          },
          { count: updatedStops.length },
        ))
      }
      if (addedItems.length === 1) {
        messages.push(intl.formatMessage(
          {
            id: '2JwfVbdF2LkiebOOWC9U',
            defaultMessage: 'Added {activityName}',
            description: 'Snackbar message when a single activity is added to the itinerary',
          },
          {
            activityName: addedItems[0].item?.name ?? intl.formatMessage({
              id: 'HAWquKfUttGOwlqZwX8E',
              defaultMessage: 'activity',
              description: 'Fallback noun for an unnamed itinerary activity in the trip planner AI chat',
            }),
          },
        ))
      } else if (addedItems.length > 1) {
        messages.push(intl.formatMessage(
          {
            id: 'Z9GKbmBRetJbmHeREI0w',
            defaultMessage: 'Added {count, plural, one {# activity} other {# activities}}',
            description: 'Snackbar message when multiple activities are added to the itinerary',
          },
          { count: addedItems.length },
        ))
      }
      if (updatedItems.length === 1) {
        messages.push(intl.formatMessage(
          {
            id: 'dIm0WdYgXEXVtrICXXMJ',
            defaultMessage: 'Updated {activityName}',
            description: 'Snackbar message when a single activity is updated in the itinerary',
          },
          {
            activityName: updatedItems[0].item?.name ?? intl.formatMessage({
              id: 'HAWquKfUttGOwlqZwX8E',
              defaultMessage: 'activity',
              description: 'Fallback noun for an unnamed itinerary activity in the trip planner AI chat',
            }),
          },
        ))
      } else if (updatedItems.length > 1) {
        messages.push(intl.formatMessage(
          {
            id: 'xGkpsy0xv_crKqLnoOR2',
            defaultMessage: 'Updated {count, plural, one {# activity} other {# activities}}',
            description: 'Snackbar message when multiple activities are updated in the itinerary',
          },
          { count: updatedItems.length },
        ))
      }

      if (messages.length > 0) {
        showSnackbar(messages[0], 'success', { icon: <LineCheckCircleIcon /> })
      }
    }

    const firstUpdate = updates[0]
    if (firstUpdate) {
      let scrollStopId: number | undefined
      let scrollDayId: number | undefined
      const scrollItemId = 'itemId' in firstUpdate ? firstUpdate.itemId : undefined

      if ('stopVisits' in firstUpdate && firstUpdate.stopVisits?.length > 0) {
        scrollStopId = firstUpdate.stopVisits[0].tripStopId
        scrollDayId = firstUpdate.stopVisits[0].startDayId
      } else if ('stopId' in firstUpdate) {
        scrollStopId = firstUpdate.stopId
        scrollDayId = 'dayId' in firstUpdate ? firstUpdate.dayId : undefined
      }

      if (scrollStopId !== undefined) {
        setScrollTarget({ stopId: scrollStopId, dayId: scrollDayId, itemId: scrollItemId })
      }
    }
  }, [queryClient, onExpandStops, intl])

  const handleEnrichmentComplete = useCallback((chatIdArg: number, event: EnrichmentCompleteEvent) => {
    // Optimistic cache patch — sets enrichmentStatus: 'enriched' on the entity so the
    // derived enriching Sets immediately exclude it. Also merges enrichment data (images, place detail).
    try {
      queryClient.setQueryData<TripItineraryData | undefined>(
        ChatKeys.itineraryDetail(chatIdArg),
        (prev) => applyEnrichmentUpdate(prev, event),
      )
    } catch {
      // Enrichment update failed, will be corrected by server response
    }
  }, [queryClient])

  const handleRefetchItinerary = useCallback((chatIdArg: number) => {
    queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatIdArg) })
  }, [queryClient])

  // Wrapper for non-streaming refetch (user actions on current chat)
  const refetchCurrentChatItinerary = useCallback(() => {
    if (chatId !== undefined) {
      queryClient.invalidateQueries({ queryKey: ChatKeys.itineraryDetail(chatId) })
    }
  }, [chatId, queryClient])

  return {
    itineraryData,
    enrichingStopIds,
    enrichingItemIds,
    scrollTarget,
    clearScrollTarget,
    handleItineraryUpdates,
    handleEnrichmentComplete,
    handleRefetchItinerary,
    refetchCurrentChatItinerary,
  }
}
